/**
 * Guest (free-to-play) identity plumbing for the web app.
 *
 * A "guest" is a pre-identity trial player: someone who adopted a free starter
 * pet before authenticating with any wallet/login. Their play session is keyed
 * by the same anonymous session id used for PVP (see pvpApi.getOrCreateSessionId)
 * turned into an address-shaped sentinel string via shared/domain/guestSession.ts
 * (`guest:<uuid>`) so it flows through existing walletAddress/owner parameters.
 *
 * localStorage access is wrapped in try/catch (private-mode / quota safe), same
 * pattern as hooks/blockchain/evmNetworkCache.ts.
 */
import { guestWalletForSession } from '@shared/domain/guestSession';
import { getOrCreateSessionId } from '../api/pvpApi';

/** localStorage key: the free pet id granted to this browser's guest session. */
export const FREE_PET_STORAGE_KEY = 'suikamon.free_pet.v1';

/** Read the free-pet marker for this browser, or null if none/unavailable. */
export function getGuestFreePetId(): string | null {
  try {
    return localStorage.getItem(FREE_PET_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist the free-pet id granted to this browser's guest session. Never throws. */
export function setGuestFreePetId(petId: string): void {
  try {
    localStorage.setItem(FREE_PET_STORAGE_KEY, petId);
  } catch {
    // private mode / quota — best-effort.
  }
}

/** Remove the free-pet marker, e.g. after the guest claims their pet into a real identity. */
export function clearGuestFreePet(): void {
  try {
    localStorage.removeItem(FREE_PET_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** The guest's address-shaped sentinel wallet (`guest:<sessionId>`). Never a real chain address. */
export function getGuestWallet(): string {
  return guestWalletForSession(getOrCreateSessionId());
}
